import {useEffect, useState} from "react";
import { useLocation, useNavigate} from "react-router-dom";
import {ArrowLeft} from "lucide-react";

interface BreadcrumbItem {
    label: string;
    href: string;
}

const breadcrumbMap: { [key: string]: string } = {
    "/dashboard": "Dashboard",
    "/architecture": "Projects",
    "/architecture/create": "Create Project",
    "/architecture/specs/[id]": "Specs Upload",
    "/architecture/generate/[id]": "Project Description",
    "/architecture/preview/[id]": "Architecture",
    "/architecture/project/[id]/services/[serviceId]": "Shreds List",
    "/architecture/project/[id]/services/[serviceId]/create": "Create Shred",
    "/architecture/project/[id]/shred/[shredId]": "Shred",
};

const resolveBreadcrumb = (pathname: string): BreadcrumbItem[] => {
    const paths = Object.keys(breadcrumbMap);
    const breadcrumbs: BreadcrumbItem[] = [];
    paths.forEach((path) => {
        const regexPath = path
            .replace(/\[([^\]]+)\]/g, "([^/]+)")
            .replace(/\//g, "\\/");
        const regex = new RegExp(`^${regexPath}$`);
        if (regex.test(pathname)) {
            const label = breadcrumbMap[path];
            breadcrumbs.push({label, href: pathname});
        }
    });
    return breadcrumbs;
};

const storeBreadcrumb = (breadcrumb: BreadcrumbItem) => {
    const stored = localStorage.getItem("breadcrumbs");
    let breadcrumbs = stored ? JSON.parse(stored) : [];
    // Find the index of the current breadcrumb in the stored breadcrumbs
    const existingIndex = breadcrumbs.findIndex(
        (item: BreadcrumbItem) => item.href === breadcrumb.href
    );
    if (existingIndex !== -1) {
        // If the breadcrumb exists, remove it and all breadcrumbs that come after it
        breadcrumbs = breadcrumbs.slice(0, existingIndex);
    }
    // Add the new breadcrumb
    breadcrumbs.push(breadcrumb);
    localStorage.setItem("breadcrumbs", JSON.stringify(breadcrumbs));
};

const Breadcrumb = () => {
    const {pathname} = useLocation();
    const navigate = useNavigate();
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

    useEffect(() => {
        if (pathname === "/dashboard") {
            // Clear all stored breadcrumbs when going back to /dashboard
            localStorage.removeItem("breadcrumbs");
            setBreadcrumbs([]);
        } else {
            if (pathname !== null) {
                const resolvedBreadcrumbs = resolveBreadcrumb(pathname);
                if (resolvedBreadcrumbs.length > 0) {
                    storeBreadcrumb(resolvedBreadcrumbs[0]);
                    setBreadcrumbs(
                        JSON.parse(localStorage.getItem("breadcrumbs") || "[]")
                    );
                }
            }
        }
    }, [pathname]);

    // Get the previous page from breadcrumbs
    const getPreviousPage = (): BreadcrumbItem | null => {
        if (breadcrumbs.length >= 2) {
            return breadcrumbs[breadcrumbs.length - 2];
        }
        return null;
    };

    const handleBackClick = () => {
        const previousPage = getPreviousPage();
        if (previousPage) {
            navigate(previousPage.href);
        } else {
            // Fallback to browser back if no previous page in breadcrumbs
            navigate(-1);
        }
    };

    const previousPage = getPreviousPage();

    // Don't show back button on dashboard or if no previous page
    if (pathname === "/dashboard" || !previousPage) {
        return null;
    }

    return (
        <nav aria-label="back navigation" className="flex items-center ml-2">
            <button
                onClick={handleBackClick}
                className="flex items-center gap-2 px-3 py-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200 group"
            >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200"/>
                <span className="font-bold text-base tracking-wide">
          Back to {previousPage.label}
        </span>
            </button>
        </nav>
    );
};

export default Breadcrumb;